// ============================================================
// Every combatant gets a marker the first time the map sees them
// ============================================================

import { useEffect, useRef } from 'react';
import { clampToBoundary } from '../../engine/battlefield/boundary';
import { tokenForSize } from '../../engine/battlefield/tokens';
import { projectDefender } from '../../engine/roster/projection';

/**
 * Where the i-th of n combatants stands on the blind ring: evenly round a
 * small circle near the arena's middle, clamped inside it.
 */
function ringSpot(boundary, i, n) {
  const angle = (i / Math.max(1, n)) * Math.PI * 2;
  const radius = Math.min(4, boundary.radius * 0.35);
  return clampToBoundary(
    boundary,
    boundary.centre.x + Math.cos(angle) * radius,
    boundary.centre.z + Math.sin(angle) * radius,
  );
}

/**
 * Put everyone the map has not placed yet on the blind ring - the fallback
 * for a session that never planted a deploy marker.
 */
function ringArrivals(roster, tokens, placed, writeToken, boundary) {
  roster.forEach((combatant, i) => {
    if (tokens[combatant.id] || placed.has(combatant.id)) return;
    placed.add(combatant.id);
    const spot = ringSpot(boundary, i, roster.length);
    // Sized from their Size trait, so a Dreadnought stands taller than
    // the wall a guardsman hides behind without anyone typing a number.
    const size = projectDefender(combatant)?.size;
    writeToken(tokenForSize(combatant.id, size, { x: spot.x, z: spot.z, rev: 1 }), { immediate: true });
  });
}

/**
 * Give every combatant a token the first time they are seen.
 *
 * Without this the map starts empty however full the roster is, and
 * someone has to place eight tokens before the feature does anything.
 * They land on a ring near the middle - a starting position, not a
 * tactical one, and the first thing anyone will do is drag them.
 *
 * The `placed` ref is what stops a race: the Firestore round trip takes
 * long enough that a re-render would otherwise place the same combatant
 * two or three times over.
 */
export function useAutoPlacedTokens({ roster, tokens, writeToken, boundary, deployArrivals }) {
  const placed = useRef(new Set());
  useEffect(() => {
    if (!writeToken) return;
    // ANYONE WHO HAS LEFT THE ROSTER IS FORGOTTEN. This ledger is a
    // debounce for the round trip - `tokens` does not show a write until
    // it comes back - and not a permanent record of everybody ever seen.
    // Treating it as one meant a recalled combatant was skipped for good:
    // they went back into the room, came out again, joined the roster and
    // never got a token, so the ⚑ button and the double-click both looked
    // like they had done nothing at all (v8.22.0).
    const onRoster = new Set(roster.map((c) => c.id));
    for (const id of placed.current) if (!onRoster.has(id)) placed.current.delete(id);
    // Anyone the map has never seen.
    const arrivals = roster.filter((c) => !tokens[c.id] && !placed.current.has(c.id));
    if (arrivals.length === 0) return;
    // The marker takes precedence when one is planted: that is the whole
    // feature. The blind ring survives only as the fallback for a session
    // that has never planted one.
    if (deployArrivals && deployArrivals(arrivals)) {
      arrivals.forEach((c) => placed.current.add(c.id));
      return;
    }
    ringArrivals(roster, tokens, placed.current, writeToken, boundary);
  }, [roster, tokens, writeToken, boundary, deployArrivals]);
}
